import { Button } from "@material-ui/core";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import ReactSelect from "react-select";
import {
  AppConfig,
  AppCurrenciesForDropdown,
  AssociateIcon,
  BachelorIcon,
  CommissionDegreeIcon,
  CommissionDueDatetimeIcon,
  CommissionFacultyIcon,
  CommissionIcon,
  CommissionLanguageIcon,
  ContractType,
  ContractTypesForDropdown,
  CreateButtonIcon,
  MedicalIcon,
  RemoveCircleIcon,
  TaxIcon,
} from "../../../../constants";
import {
  _getCheckbox,
  _getMaterialSelectInput,
  _getNewSelectInput,
  _getNormalInput,
} from "../../../../libs";

function AgreementEditForm({ defaultValues, dropdownsData, onSave, props }) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    getValues,
  } = useForm({
    defaultValues,
  });

  console.log({ defaultValues });

  // Reset form when defaultValues change (e.g. switching item)
  useEffect(() => {
    console.log("in effect default value");
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Set up field array for commissions
  const {
    fields: commissionsFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "commissions",
  });

  // Watch contract type for conditional rendering
  const isContractSpecific =
    useWatch({ name: "contracttype", control }) === ContractType.Specific;

  const currentBonus = getValues("bonus");

  // Replace setValue-based add/remove with useFieldArray methods
  const handleAddNewRow = () => {
    append({
      active: false,
      degree: null,
      facultyid: null,
      commissionduetime: null,
      commission: "",
      commissionnature: "",
      language: "nolanguage",
    });
  };

  const handleRemoveRow = (index) => {
    remove(index); // This updates both form and field array state
  };

  return (
    <form onSubmit={handleSubmit(onSave)} className="mt-3">
      {/* ... TAX, Contract Type, Add Term Button ... */}
      <div className="row">
        <div className="col-11 d-flex justify-content-end mb-2">
          <Controller
            name="contracttype"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <ReactSelect
                {...field}
                className="itqan-react-select-field"
                options={ContractTypesForDropdown}
                placeholder={window.sys_app_translate(
                  "Contract Type",
                  props.user.lang
                )}
                value={ContractTypesForDropdown.find(
                  (opt) => opt.value === field.value
                )}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);

                  if (selectedOption.value === ContractType.Standard) {
                    // Clear language fields when switching back to Standard
                    const currentCommissions = getValues("commissions");

                    setValue(
                      "commissions",
                      currentCommissions.map((comm) =>
                        comm.language !== "nolanguage"
                          ? { ...comm, language: "nolanguage" }
                          : comm
                      )
                    );
                  }
                }}
              />
            )}
          />
          {_getNormalInput({
            control,
            inputName: "tax",
            errors,
            inputTitle: "KDV",
            lang: props.user.lang,
            type: "number",
            suffix: "%",
            width: "col-2 mx-3",
            prefix: <TaxIcon />,
            inputSize: "small",
          })}
          <Button
            variant="contained"
            style={{
              backgroundColor: AppConfig.themeColors.secondary,
              color: "white",
              width: "10rem",
            }}
            startIcon={<CreateButtonIcon />}
            className="inter inter-bold"
            onClick={handleAddNewRow}
          >
            {window.sys_app_translate("Add Term", props.user.lang)}
          </Button>
        </div>
      </div>
      {/* Commissions Section */}
      {commissionsFields.length > 0 && (
        <div className="row mb-3">
          {/* Active Toggle, Headeing */}
          <div className="d-inline-flex align-items-center gap-5 mb-4">
            <h3
              className="inter inter-bold"
              style={{ color: "var(--secondary)" }}
            >
              {window.sys_app_translate("Commissions", props.user.lang)}:
            </h3>
            <div className="form-check form-switch table-activate-button d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                id="commissionenabled"
                role="switch"
                {...register("commissionenabled")}
              />
              <label
                className="form-check-label inter inter-medium"
                htmlFor="commissionenabled"
                style={{
                  color: "var(--primary)",
                  marginRight: "18px",
                  marginLeft: "6px",
                  fontSize: "1.2rem",
                }}
              >
                {window.sys_app_translate("Active", props.user.lang)}
              </label>
            </div>
          </div>

          {/* Render Rows */}
          {commissionsFields.map((field, index) => (
            <div key={field.id} className="row d-flex align-items-center mb-4">
              {_getCheckbox({
                inputName: `commissions.${index}.active`,
                register,
                inputId: `commissions.${index}.active`,
                lang: props.user.lang,
                width: "w-auto",
              })}

              {isContractSpecific &&
                _getNewSelectInput({
                  control,
                  inputName: `commissions.${index}.language`,
                  options: [
                    { label: "English", value: "English" },
                    { label: "Turkish", value: "Turkish" },
                  ],
                  errors,
                  inputTitle: "Language",
                  lang: props.user.lang,
                  width: "col-2",
                  startIcon: <CommissionLanguageIcon />,
                  customValidator: (value) =>
                    value !== "nolanguage" || "Please select a valid language",
                })}

              {_getNewSelectInput({
                control,
                inputName: `commissions.${index}.degree`,
                options: dropdownsData.degreesDropdownGet || [],
                errors,
                inputTitle: "Degree",
                lang: props.user.lang,
                width: "col-2",
                startIcon: <CommissionDegreeIcon />,
              })}

              {_getNewSelectInput({
                control,
                inputName: `commissions.${index}.facultyid`,
                options: dropdownsData.facultiesDropdownGet || [],
                errors,
                inputTitle: "Faculty",
                lang: props.user.lang,
                width: "col-2",
                startIcon: <CommissionFacultyIcon />,
              })}

              {_getNewSelectInput({
                control,
                inputName: `commissions.${index}.commissionduetime`,
                options: dropdownsData.commissionDueDatetimeGet || [],
                errors,
                inputTitle: "Type",
                lang: props.user.lang,
                width: "col-2",
                startIcon: <CommissionDueDatetimeIcon />,
              })}

              {_getNormalInput({
                control,
                inputName: `commissions.${index}.commission`,
                inputTitle: "Commission",
                errors,
                lang: props.user.lang,
                type: "number",
                width: "col-2",
                inputSize: "medium",
                required: true,
                prefix: <CommissionIcon />,
                suffix: _getMaterialSelectInput({
                  control,
                  inputName: `commissions.${index}.commissionnature`,
                  options: [
                    { label: "%", value: "%" },
                    ...AppCurrenciesForDropdown,
                  ],
                  errors,
                  lang: props.user.lang,
                  width: "col-4",
                  required: true,
                }),
              })}

              <div className="col-1">
                <RemoveCircleIcon
                  onClick={() => handleRemoveRow(index)}
                  className="clickable"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bonus Section… */}
      {currentBonus && Object.keys(currentBonus).length > 0 && (
        <div className="row">
          {/* <span class="inter inter-bold" style="font-size: 1.5rem;">Agreement</span> */}
          <div className="d-inline-flex align-items-center gap-5 justify-content-start mb-4">
            <h3
              className="inter inter-bold"
              style={{ color: "var(--secondary)" }}
            >
              {window.sys_app_translate("Bonus", props.user.lang)}:
            </h3>

            <div>
              <div className="form-check form-switch table-activate-button d-flex align-items-center">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="bonusenabled"
                  role="switch"
                  {...register("bonusenabled")}
                />
                <label
                  className="form-check-label inter inter-medium"
                  htmlFor="bonusenabled"
                  style={{
                    color: "var(--primary)",
                    marginRight: "18px",
                    marginLeft: "6px",
                    fontSize: "1.2rem",
                  }}
                >
                  {window.sys_app_translate("Active", props.user.lang)}
                </label>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row d-flex justify-content-start align-items-center">
              {_getNormalInput({
                control,
                inputName: "bonus.associate",
                inputTitle: "Associate",
                inputId: "Associate",
                errors,
                lang: props.user.lang,
                type: "number",
                width: "col-2",
                inputSize: "medium",
                required: isContractSpecific,
                prefix: <AssociateIcon />,
                suffix: _getMaterialSelectInput({
                  control: control,
                  inputName: "bonus.associatenature",
                  options: [
                    { label: "%", value: "%" },
                    ...AppCurrenciesForDropdown,
                  ],
                  errors: errors,
                  lang: props.user.lang,
                  width: "col-4",
                  required: isContractSpecific,
                  onChange: (e) => {},
                  disabled: false,
                  defaultValue: null,
                }),
              })}

              {_getNormalInput({
                control,
                inputName: "bonus.bachelor",
                inputTitle: "Bachelor",
                inputId: "Bachelor",
                errors,
                lang: props.user.lang,
                type: "number",
                width: "col-2",
                inputSize: "medium",
                required: isContractSpecific,
                prefix: <BachelorIcon />,
                suffix: _getMaterialSelectInput({
                  control: control,
                  inputName: "bonus.bachelornature",
                  options: [
                    { label: "%", value: "%" },
                    ...AppCurrenciesForDropdown,
                  ],
                  errors: errors,
                  lang: props.user.lang,
                  width: "col-3",
                  required: isContractSpecific,
                  onChange: (e) => {},
                  disabled: false,
                  defaultValue: null,
                }),
              })}

              {_getNormalInput({
                control,
                inputName: "bonus.master",
                inputTitle: "Master",
                inputId: "Master",
                errors,
                lang: props.user.lang,
                type: "number",
                width: "col-2",
                inputSize: "medium",
                required: isContractSpecific,
                prefix: <BachelorIcon />,
                suffix: _getMaterialSelectInput({
                  control: control,
                  inputName: "bonus.masternature",
                  options: [
                    { label: "%", value: "%" },
                    ...AppCurrenciesForDropdown,
                  ],
                  errors: errors,
                  lang: props.user.lang,
                  width: "col-3",
                  required: isContractSpecific,
                  onChange: (e) => {},
                  disable: false,
                  defaultValue: null,
                }),
              })}

              {_getNormalInput({
                control,
                inputName: "bonus.phd",
                inputTitle: "PHD",
                inputId: "PHD",
                errors,
                lang: props.user.lang,
                type: "number",
                width: "col-2",
                inputSize: "medium",
                required: isContractSpecific,
                prefix: <BachelorIcon />,
                suffix: _getMaterialSelectInput({
                  control: control,
                  inputName: "bonus.phdnature",
                  options: [
                    { label: "%", value: "%" },
                    ...AppCurrenciesForDropdown,
                  ],
                  errors: errors,
                  lang: props.user.lang,
                  width: "col-3",
                  required: isContractSpecific,
                  onChange: (e) => {},
                  disabled: false,
                  defaultValue: null,
                }),
              })}

              {_getNormalInput({
                control,
                inputName: "bonus.medical",
                inputTitle: "Medical",
                inputId: "Medical",
                errors,
                lang: props.user.lang,
                width: "col-2",
                inputSize: "medium",
                required: isContractSpecific,
                prefix: <MedicalIcon />,
                suffix: _getMaterialSelectInput({
                  control: control,
                  inputName: "bonus.medicalnature",
                  options: [
                    { label: "%", value: "%" },
                    ...AppCurrenciesForDropdown,
                  ],
                  errors: errors,
                  lang: props.user.lang,
                  width: "col-3",
                  required: isContractSpecific,
                  onChange: (e) => {},
                  disabled: false,
                  defaultValue: null,
                }),
              })}
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="col-11 d-flex justify-content-end my-3">
        <Button
          type="submit"
          variant="contained"
          style={{
            backgroundColor: AppConfig.themeColors.primary,
            color: "white",
            width: "10rem",
          }}
          className="inter inter-bold"
        >
          {window.sys_app_translate("Save", props.user.lang)}
        </Button>
      </div>
    </form>
  );
}

export default AgreementEditForm;
